import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useProductFilter } from '@gripp/shared-logic';
import { BaseColumnCheckbox, Colors } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { ModalButton } from '../../shared/components/modalButton';
import {
    ProductAssignmentForm,
    ProductAssignmentType,
} from './productAssignmentForm';

export const useColumnsProduct = (showCheckboxColumn: boolean) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper();

  const { data: productFilter } = useProductFilter();

  const [columns, setColumns] = useState<ColumnDef<unknown, never>[]>([]);

  const allColumns: Record<string, ColumnDef<unknown, never>> = useMemo(
    () => ({
      selected: columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: { style: styles.checkboxCell },
      }),
      name: columnHelper.accessor('name', {
        cell: (info) => {
          const name = info.getValue();
          return <Text style={styles.cellText}>{name}</Text>;
        },
        header: () => t('product.list.name'),
        meta: { sortable: true },
      }),
      description: columnHelper.accessor('description', {
        cell: (info) => {
          const description = info.getValue();
          return <Text style={styles.cellText}>{description}</Text>;
        },
        header: () => t('product.list.description'),
        meta: { sortable: true },
      }),

      routines: columnHelper.accessor('routines', {
        cell: (info) => {
          const routines = info.getValue() as any[];
          const row: any = info.row.original;
          return (
            <ModalButton
              label=""
              customButton={
                <View style={styles.routine}>
                  <MaterialCommunityIcons
                    name="format-list-checks"
                    size={24}
                    style={styles.routineIcon}
                  />
                  <Text style={styles.cellText}>{routines?.length || 0}</Text>
                </View>
              }
              modalComponent={({ onComplete }) => (
                <ProductAssignmentForm
                  productId={row.id}
                  title={row.name}
                  onComplete={onComplete}
                  assignmentType={ProductAssignmentType.Routines}
                />
              )}
              containerStyle={styles.modalContainer}
            />
          );
        },
        header: () => t('product.list.routines'),
        meta: { sortable: true },
      }),
      alerts: columnHelper.accessor('alerts', {
        cell: (info) => {
          const alerts = info.getValue() || 0;
          return (
            <View style={styles.countContainer}>
              <MaterialCommunityIcons
                name="alarm-check"
                size={20}
                style={styles.countIcon}
              />
              <Text style={styles.cellText}>{alerts}</Text>
            </View>
          );
        },
        header: () => t('product.list.alerts'),
        meta: { sortable: true },
      }),
      assetsCount: columnHelper.accessor('assetsCount', {
        cell: (info) => {
          const assetsCount = info.getValue();
          return (
            <View style={styles.countContainer}>
              <MaterialCommunityIcons
                name="qrcode"
                size={20}
                style={styles.countIcon}
              />
              <Text style={styles.cellText}>{assetsCount}</Text>
            </View>
          );
        },
        header: () => t('product.list.assetsCount'),
        meta: { sortable: true },
      }),
      filesCount: columnHelper.accessor('filesCount', {
        cell: (info) => {
          const filesCount = info.getValue();
          const row: any = info.row.original;
          return (
            <ModalButton
              label=""
              customButton={
                <View style={styles.countContainer}>
                  <MaterialCommunityIcons
                    name="file-document"
                    size={20}
                    style={styles.countIcon}
                  />
                  <Text style={styles.cellText}>{filesCount}</Text>
                </View>
              }
              modalComponent={({ onComplete }) => (
                <ProductAssignmentForm
                  productId={row.id}
                  title={row.name}
                  onComplete={onComplete}
                  assignmentType={ProductAssignmentType.Files}
                />
              )}
              containerStyle={styles.modalContainer}
            />
          );
        },
        header: () => t('product.list.filesCount'),
        meta: { sortable: true },
      }),
      addonsCount: columnHelper.accessor('addonsCount', {
        cell: (info) => {
          const addonsCount = info.getValue() || 0;
          const row: any = info.row.original;
          return (
            <ModalButton
              label=""
              customButton={
                <View style={styles.countContainer}>
                  <MaterialCommunityIcons
                    name="shape-outline"
                    size={20}
                    style={styles.countIcon}
                  />
                  <Text style={styles.cellText}>{addonsCount}</Text>
                </View>
              }
              modalComponent={({ onComplete }) => (
                <ProductAssignmentForm
                  productId={row.id}
                  title={row.name}
                  onComplete={onComplete}
                  assignmentType={ProductAssignmentType.Addons}
                />
              )}
              containerStyle={styles.modalContainer}
            />
          );
        },
        header: () => t('product.list.addonsCount'),
        meta: { sortable: true },
      }),
      createdAt: columnHelper.accessor('createdAt', {
        cell: (info) => {
          const value = info.getValue() as any;
          if (value) {
            // Handle both Date objects and strings
            const dateValue =
              value instanceof Date ? value.toISOString() : value;
            const date = moment(dateValue);
            if (date.isValid()) {
              return <Text style={styles.cellText}>{date.fromNow()}</Text>;
            }
          }
          return <Text style={styles.cellText}>-</Text>;
        },
        header: () => t('product.list.createdAt'),
        meta: {
          sortable: true,
          exportable: true,
          exportCell: (data: any) => {
            if (data.createdAt) {
              const dateValue =
                data.createdAt instanceof Date
                  ? data.createdAt.toISOString()
                  : data.createdAt;
              const date = moment(dateValue);
              if (date.isValid()) {
                return date.format('YYYY-MM-DD HH:mm:ss');
              }
              return dateValue || '';
            }
            return '';
          },
        },
      }),
      updatedAt: columnHelper.accessor('updatedAt', {
        cell: (info) => {
          const value = info.getValue() as any;
          if (value) {
            // Handle both Date objects and strings
            const dateValue =
              value instanceof Date ? value.toISOString() : value;
            const date = moment(dateValue);
            if (date.isValid()) {
              return <Text style={styles.cellText}>{date.fromNow()}</Text>;
            }
          }
          return <Text style={styles.cellText}>-</Text>;
        },
        header: () => t('product.list.updatedAt'),
        meta: {
          sortable: true,
          exportable: true,
          exportCell: (data: any) => {
            if (data.updatedAt) {
              const dateValue =
                data.updatedAt instanceof Date
                  ? data.updatedAt.toISOString()
                  : data.updatedAt;
              const date = moment(dateValue);
              if (date.isValid()) {
                return date.format('YYYY-MM-DD HH:mm:ss');
              }
              return dateValue || '';
            }
            return '';
          },
        },
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productFilter]
  );

  useEffect(() => {
    if (!productFilter) return;

    const columnList = [];
    if (showCheckboxColumn) {
      columnList.push(allColumns.selected);
    }

    columnList.push(allColumns.name);

    // Add columns in specific order: description, assetsCount, routines, alerts, filesCount, addonsCount, createdAt, updatedAt
    const columnOrder = [
      'description',
      'assetsCount',
      'routines',
      'alerts',
      'filesCount',
      'addonsCount',
      'createdAt',
      'updatedAt',
    ];

    columnOrder.forEach((key) => {
      if (productFilter.columns?.includes(key) && allColumns[key]) {
        columnList.push(allColumns[key]);
      }
    });

    setColumns(columnList);
  }, [productFilter, allColumns, showCheckboxColumn]);

  return columns;
};

const styles = StyleSheet.create({
  productsContainer: {
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
  emailText: {
    overflow: 'hidden',
  },
  routine: { flexDirection: 'row', alignItems: 'center' },
  routineIcon: {
    marginRight: 6,
    color: Colors.blue,
  },
  countContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  countIcon: {
    marginRight: 6,
  },
});
